/**
 * App.tsx - Application Root
 *
 * INITIALIZATION PATTERN:
 * -----------------------
 * This file handles GLOBAL INFRASTRUCTURE initialization that:
 * - Runs once at app startup (before React renders)
 * - Does NOT depend on user authentication
 * - Applies to ALL users (authenticated and unauthenticated)
 *
 * Initializations in this file:
 * - PostHog analytics (session recording, autocapture)
 * - Sentry error tracking (performance monitoring, replay)
 * - Userflow onboarding
 * - API base URLs (OpenAPI clients)
 * - Global error handlers (window.onerror, unhandledrejection)
 * - PWA/Service Worker registration
 * - Loki logger
 *
 * For USER-SPECIFIC initializations (that require authentication):
 * - See: src/routes/PrivateRouter/index.tsx
 */
import { OpenAPI as AppClientOpenAPI } from '@LoopKitchen/app-client'
import { ThemeProvider } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import React, { Suspense, useEffect } from 'react'
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import theme from 'src/theme/defaultTheme'
import { POST_HOG_BASE_URL } from './assets/configs/globalConst'
import Loading from './components/Loading'
import { AgentOpenAPI, OpenAPI, TruROIAgentOpenAPI } from './services/openApiV2'
import { APP_SERVICE_BASE_URL, APP_VERSION, BASE_AGENT_URL, BASE_API_URL, TRUROI_AGENT_BASE_URL } from './utils/config/config'
import history from './utils/config/history'

// Context Providers
import ErrorContextProvider from 'src/context/ErrorContext'
import LoadingContextProvider from 'src/context/LoadingContext'
import OrderContextProvider from 'src/context/OrderContext'
import SnackContextProvider from 'src/context/SnackContext'
import Router from 'src/routes/Routes'
import { SentryErrorBoundary } from './components/SentryErrorBoundary'
import BugReportContextProvider from './context/BugReportContext'
import PublicAuthContextProvider from './context/PublicAuthContext'

// PWA and Service Worker imports
import { OfflineIndicator } from './utils/sw/offlineHooks'
import { clearAllCaches, pwaManager } from './utils/sw/pwaUtils'

// Sentry Initialization
import * as Sentry from '@sentry/react'

// PostHog Initialization
import posthog from 'posthog-js'

// Loki Logger
import { lokiLogger } from './services/lokiLogger'

// Userflow
import userflow from 'userflow.js'

// Initialize monitoring before any other imports
if (process.env.NODE_ENV === 'production') {
  // Initialize PostHog with comprehensive configuration
  // @ts-ignore - PostHog types may be outdated
  posthog.init('phc_sikWFIgkDh7HTtJ6C3Gx5CTU3DvS21g5acZ8lZ9HRyi', {
    api_host: POST_HOG_BASE_URL,
    // Autocapture - automatically track clicks, form submissions, pageviews
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    // Session Recording with network request bodies for debugging
    enable_recording_console_log: true,
    session_recording: {
      maskAllInputs: false,
      maskNetworkRequestFn: (request) => request,
      // Capture network request/response bodies for RCA debugging
      recordBody: true,
      recordHeaders: true,
      // Enable canvas recording for loop sheets
      captureCanvas: {
        canvasFps: 12, // Frames per second (0-12, default 2)
        canvasQuality: '0.6' // Quality as string (0.0-1.0, default 0.4)
      }
    },
    // Performance and reliability
    persistence: 'localStorage+cookie',
    bootstrap: {
      distinctID: undefined // Will be set on identify
    },
    // Privacy settings
    respect_dnt: false, // We need analytics for business
    secure_cookie: true
  })

  // Initialize Sentry last with comprehensive monitoring
  Sentry.init({
    dsn: 'https://31ca9b004e6d439181967f14e7f76394@o4504559796682752.ingest.sentry.io/4504650246062080',
    integrations: [
      // Browser Tracing for performance monitoring and distributed tracing
      Sentry.browserTracingIntegration({
        enableInp: true // Track Interaction to Next Paint
      }),
      // Session Replay for debugging user sessions with network capture
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Network capture for RCA debugging - capture request/response bodies
        networkDetailAllowUrls: [
          /^https:\/\/.*\.tryloop\.ai/,
          /^https:\/\/.*\.loopapplication\.xyz/
        ],
        networkRequestHeaders: ['X-Request-ID', 'Content-Type', 'Authorization'],
        networkResponseHeaders: ['X-Request-ID', 'Content-Type', 'X-Response-Time'],
        networkCaptureBodies: true
      }),
      // @ts-ignore - PostHog types may be outdated
      posthog.sentryIntegration({
        organization: 'loop-frontend',
        projectId: '4504650246062080'
      })
    ],
    // Performance monitoring - sample 100% of transactions
    tracesSampleRate: 1.0,
    // Session Replay - capture 10% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Distributed tracing - propagate trace headers to these APIs
    // Note: Exclude functions.tryloop.ai as Firebase Functions CORS doesn't allow baggage/sentry-trace headers
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/(?!functions\.).*\.tryloop\.ai/, // Exclude functions.tryloop.ai
      /^https:\/\/.*\.loopapplication\.xyz/
    ],
    // Error sampling
    sampleRate: 1.0,
    // Additional configuration for better error tracking
    environment: process.env.NODE_ENV,
    release: APP_VERSION,
    // Disable debug in production
    debug: false,
    attachStacktrace: true,
    // Configure error filtering
    beforeSend(event: any, hint: any) {
      // Filter out known non-critical errors (auto-recovered by reload)
      if (event.exception) {
        const error = hint.originalException as Error
        if (
          error?.message?.includes('ChunkLoadError') ||
          error?.message?.includes('Loading chunk') ||
          error?.message?.includes('Unable to preload CSS') ||
          error?.message?.includes('Failed to fetch dynamically imported module') ||
          error?.message?.includes('ResizeObserver loop limit exceeded') ||
          // Workbox IndexedDB race condition - non-critical, caching still works
          error?.message?.includes('database connection is closing') ||
          error?.message?.includes('InvalidStateError') ||
          error?.name === 'InvalidStateError'
        ) {
          return null // Don't send these errors - auto-recovered or non-critical
        }
      }
      return event
    },
    // Configure performance event filtering
    beforeSendTransaction(event: any) {
      // Filter out very fast transactions that aren't useful
      if (event.transaction && event.start_timestamp && event.timestamp) {
        const duration = event.timestamp - event.start_timestamp
        if (duration < 0.01) {
          // Less than 10ms
          return null
        }
      }
      return event
    },
    // Set user context automatically
    initialScope: {
      tags: {
        component: 'loop-frontend',
        version: APP_VERSION
      }
    }
  })
}

// Initialize Userflow
userflow.init(process.env.NODE_ENV === 'production' ? 'ct_us1_fnv36puzerbfllnvq5fyj5uds4' : 'ct_us1_hjgcuiq73vc2zknm6gmlafuq5a')

// Set API Base URLs
OpenAPI.BASE = BASE_API_URL
AgentOpenAPI.BASE = BASE_AGENT_URL
TruROIAgentOpenAPI.BASE = TRUROI_AGENT_BASE_URL
AppClientOpenAPI.BASE = APP_SERVICE_BASE_URL

/**
 * Setup global error handlers for unhandled errors
 */
const setupGlobalErrorHandlers = (): void => {
  // Handle unhandled errors
  window.addEventListener('error', (event: ErrorEvent) => {
    console.error('Unhandled error:', event.error)

    // Log to Loki
    lokiLogger.logUnhandledError(event, 'window.onerror')

    // Let Sentry handle it as well
    // Return false to allow default error handling
    return false
  })

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    console.error('Unhandled promise rejection:', event.reason)

    // Log to Loki
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    lokiLogger.logUnhandledError(error, 'unhandledrejection')

    // Let Sentry handle it as well
    // Return false to allow default error handling
    return false
  })
}

/**
 * Setups up automatic Google cache clearing on auth errors
 */
const setupAuthErrorHandling = (): void => {
  let authFailureCount = 0
  const lastAuthCheck = Date.now()

  // Listen for auth errors in Google iframes or popups
  window.addEventListener('message', (event) => {
    try {
      // Only process messages from Google domains
      if (!event.origin.includes('google') && !event.origin.includes('accounts.google.com') && !event.origin.includes('gstatic.com')) {
        return
      }

      // Check for auth errors in the message
      const message = typeof event.data === 'string' ? event.data : JSON.stringify(event.data)

      if (message.includes('error') || (message.includes('auth') && message.includes('fail')) || message.includes('denied') || message.includes('cancelled')) {
        console.warn('Potential Google auth error detected, clearing caches...')
        clearAllCaches()
      }
    } catch (err) {
      // Ignore parsing errors
    }
  })

  // Watch for failed Google logins via URL parameters
  const checkUrlForAuthErrors = (): void => {
    const url = window.location.href
    if (
      url.includes('error=') ||
      url.includes('access_denied') ||
      url.includes('auth_failure') ||
      (url.includes('/auth/google/callback') && url.includes('error'))
    ) {
      console.warn('Auth error detected in URL, clearing Google caches...')
      clearAllCaches()

      // Track auth failures to detect persistent issues
      authFailureCount++

      // If multiple failures happen in a short time, try a more aggressive cleanup
      if (authFailureCount > 2 && Date.now() - lastAuthCheck < 300000) {
        // 5 minutes
        console.warn('Multiple auth failures detected, performing full cache reset')
        clearAllCaches()
      }
    }
  }

  // Check right away and when URL changes
  checkUrlForAuthErrors()

  // Listen for route changes using browser history API
  // Cast to any to access the native history object's methods
  const browserHistory = window.history
  const originalPushState = browserHistory.pushState
  browserHistory.pushState = function (...args: any[]) {
    originalPushState.apply(this, args)
    checkUrlForAuthErrors()
  }

  window.addEventListener('popstate', checkUrlForAuthErrors)
}

/**
 * Initialize PWA functionality using the refactored PWA manager
 * Note: Asset error handling is initialized in index.tsx via initializeAssetErrorHandling()
 */
const initializePWA = (): void => {
  // Skip PWA initialization completely in development mode
  if (import.meta.env.DEV) {
    return
  }

  // Initialize service worker only if PWA is enabled
  if (pwaManager.serviceWorker.isEnabled()) {
    const currentVersion = pwaManager.version.getCurrentVersion()
    const storedVersion = pwaManager.version.getStoredVersion()

    // Only clear caches if we have a valid current version and it's different from stored version
    if (currentVersion !== 'unknown' && storedVersion && currentVersion !== storedVersion) {
      // Store the new version in sessionStorage before clearing caches
      pwaManager.version.storeVersion(currentVersion)

      // Clear caches (sessionStorage is always preserved now)
      pwaManager.cache.clearAllCaches(false, true).catch(() => {})
    } else if (!storedVersion && currentVersion !== 'unknown') {
      // First run or version was cleared - just store the current version
      pwaManager.version.storeVersion(currentVersion)
    }
  } else {
    // Clear all caches when PWA is disabled, but don't do this on every reload
    // Only clear if we haven't already done so (check a session flag)
    if (!sessionStorage.getItem('pwa_caches_cleared')) {
      pwaManager.cache
        .clearAllCaches()
        .then(() => {
          sessionStorage.setItem('pwa_caches_cleared', 'true')
        })
        .catch((error) => {
          console.error('Failed to clear caches:', error)
        })
    }
  }
}

const App = React.memo(() => {
  useEffect(() => {
    // Initialize PWA functionality (version management, cache clearing)
    // Note: Asset error handling is initialized in index.tsx via initializeAssetErrorHandling()
    initializePWA()

    // Setup global error handlers for unhandled errors (Loki logging)
    setupGlobalErrorHandlers()

    // Setup auth error handling for Google login
    setupAuthErrorHandling()

    // Set initial Sentry context
    Sentry.setTag('app.version', APP_VERSION)
    Sentry.setTag('app.environment', process.env.NODE_ENV)
    Sentry.setContext('app', {
      version: APP_VERSION,
      build: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    })

    // Cleanup on unmount
    return () => {
      // Cleanup if needed
    }
  }, [])

  return (
    <>
      <div id="recaptcha-container" />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        limit={5}
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        closeButton={true}
        theme="colored"
      />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SentryErrorBoundary>
          {/* @ts-expect-error */}
          <HistoryRouter history={history}>
            <LoadingContextProvider>
              <SnackContextProvider>
                <ErrorContextProvider>
                  <PublicAuthContextProvider>
                    <OrderContextProvider>
                      <BugReportContextProvider>
                        <Suspense fallback={<Loading />}>
                          <Router />
                          <OfflineIndicator />
                        </Suspense>
                      </BugReportContextProvider>
                    </OrderContextProvider>
                  </PublicAuthContextProvider>
                </ErrorContextProvider>
              </SnackContextProvider>
            </LoadingContextProvider>
          </HistoryRouter>
        </SentryErrorBoundary>
      </ThemeProvider>
    </>
  )
})

export default App
